import React, { useState, useEffect } from 'react';
import { Button, Card, Col, Container, Form, Row} from "react-bootstrap";
import { PencilIcon } from '@primer/octicons-react'
import { axiosAuth_PLATFORM_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import classes from './AdminPages.module.css';
import { translateAPIError } from '../utils/apiUtils';
import { testJSON } from '../utils/jsUtils';
import { formatDate } from '../utils/timeUtils';


const BizConfigAdminDetailsView = (props) => {

  // Utilities
  const { t } = useTranslation(['common', 'custom']);

  // Initialize with actual JSON obj.  If not, will get error.
  const [objAttrs, setObjAttrs] = useState({
    id: '',
    name: '',
    description: ''
  });

  const [currentAttrs, setCurrentAttrs] = useState(null);


  // View vs Edit Modes
  const [editMode, setEditMode] = useState(false);

  const [errMsg,     setErrMsg]     = useState('');
  const [errMsgEdit, setErrMsgEdit] = useState('');


  // UI Functions

  // Initialize state
  useEffect(() => {
    if (props) {
      setObjAttrsWithFormatting (props.objDetails);
    }
  }, [props]);


  const setObjAttrsWithFormatting = (objDetails) => {

    // Properly format receiver settings JSON
    // Makes a deep copy
    let inputDetails = JSON.parse (JSON.stringify (objDetails));

    inputDetails.config = (objDetails.config === null) ? '' :
                          JSON.stringify(JSON.parse(JSON.stringify(objDetails.config)), undefined, 4);

    setObjAttrs(inputDetails);
    setCurrentAttrs(inputDetails);
  }

  const handleEditAttrs = (e) => {
    e.preventDefault();
    setEditMode(true);
    setErrMsg('');
  }


  const onAttrsInputChange = (e) => {
    setObjAttrs({ ...objAttrs, [e.target.name]: e.target.value })
  }

  const handleEditCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setObjAttrs (currentAttrs);

    setEditMode(false);
    setErrMsgEdit('');
  }

  const handleEditSubmit = (e) => {
    e.preventDefault();

    if (!objAttrs.name || objAttrs.name.trim().length === 0) {
      setErrMsgEdit(t("I18N_ERR_Need_name"));
    } else if (objAttrs.name.length > 50) {
      setErrMsgEdit(t("I18N_ERR_Max_len_name") + "50");
    } else if (objAttrs.name !== currentAttrs.name) {
      validateName();
    } else {
      validateOtherFields();
    }

    return;
  }

  // Since this is async, need to call this first, and check the others when username availability returns
  function validateName() {

    new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_PLATFORM_API.get("business-configs?name=" + objAttrs.name);

        resolve(res.data);

        if ((res.status === 200) && (res.data.length > 0)) {
          // Model name is taken
          setErrMsgEdit(t("I18N_ERR_Name_taken"));
          return;
        } else {
          validateOtherFields();
          return;
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsgEdit(t(translateAPIError(error)));
          validateOtherFields();
        }
        return;
      }
    });
  }

  function validateOtherFields() {
    // Validate form inputs

    if (!objAttrs.description || objAttrs.description.trim().length === 0) {
      setErrMsgEdit(t("I18N_ERR_Need_description"));
    } else if (objAttrs.description.length > 200) {
      setErrMsgEdit(t("I18N_ERR_Max_len_desc") + "200");

    } else if (!objAttrs.config || objAttrs.config.length === 0) {
      setErrMsgEdit(t("I18N_ERR_Need_config"));
    } else if (   (objAttrs.config !== '')
               && (!testJSON(objAttrs.config.trim()))) {
      // Validate configuration to be valid JSON
      setErrMsgEdit(t("I18N_ERR_Config_need_valid_JSON"));
    } else {
      updateObj();
    }
  }

  const updateObj = () => {

    const reqBody = objAttrs;

    if ((objAttrs.config == null) || (objAttrs.config === '')) {
      reqBody.config = null;
    } else {
      reqBody.config = JSON.parse(objAttrs.config.trim());
    }

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_PLATFORM_API.put("business-configs/" + objAttrs.id, reqBody);

        resolve(res.data);

        if (res.status === 200) {
          // Restore UI to viewing mode
          setEditMode(false);
          setErrMsgEdit('');

          fetchBizConfig(objAttrs.id);
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsgEdit(t(translateAPIError(error)));
        }
      }
    });

  }


  const fetchBizConfig = (id) => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_PLATFORM_API.get("business-configs/" + id);

        resolve(res.data);

        if (   (res.status === 200)
            && (res.data && res.data.length > 0)) {
          setObjAttrsWithFormatting(res.data[0]);
        }

        setErrMsg('');
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }


  const handleClose = (e) => {
    e.preventDefault();

    // Return to list
    props.onUIMode('LIST')
  }


  return (
    <>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', fontSize:'13px'}}>

        <div style={{display:'flex'}}>
        <span className={classes.admin_pages__page_title}>
          {t("I18N_BizConfig_Details")}
        </span>
        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div display='flex'>
            <span>
              {t("I18N_Config_info")}
            </span>
            { props.objDetails.hasUi ?
              <span style={{marginLeft:'15px', fontSize:'11px', color:'SteelBlue'}}>
                {t("I18N_Use_dedicated_ui_to_edit")}
              </span>
            :
              <>
                {editMode ?
                  null
                  :
                  <span style={{marginLeft:'15px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditAttrs}>
                    <PencilIcon size={12} />
                    {t("I18N_Edit")}
                  </span>
                }
              </>
            }
          </div>
          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

          {editMode ?
            <Container>
              <form>
                <Row className={classes.admin_pages__field_label}>
                  <Col md={3}>{t("I18N_Name")}: *</Col>
                  <Col md={3}>{t("I18N_Description")}:</Col>
                  <Col md={3}></Col>
                  <Col md={3}></Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col md={3}>
                    <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="name" value={objAttrs.name}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col md={3}>
                    <textarea rows={4} className={classes.admin_pages__field_editable} style={{width:'100%'}} name="description" value={objAttrs.description}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col md={3}>
                  </Col>
                  <Col md={3}>
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  <Col sm={{span: 12}}>{t("I18N_Config")}: *</Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col sm={{span: 12}}>
                    <textarea className={classes.admin_pages__field_editable} rows={10} style={{minWidth: '100%', minHeight:'400px'}} name="config"
                              value={objAttrs.config} onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px', fontStyle:'italic'}}>
                  <span>{t("I18N_Req_fields")}</span>
                </Row>

                <Row>
                  <div display='flex' style={{marginTop:'15px'}}>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleEditCancel}>
                      {t("I18N_Cancel")}
                    </Button>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ marginLeft: '15px' }}
                      onClick={handleEditSubmit}>
                      {t("I18N_Save")}
                    </Button>
                    <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                      {errMsgEdit}
                    </span>
                  </div>
                </Row>
              </form>
            </Container>
            :
            <Container>
              <Row className={classes.admin_pages__field_label}>
                <Col md={3}>{t("I18N_Name")}:</Col>
                <Col md={3}>{t("I18N_Description")}:</Col>
                <Col md={3}></Col>
                <Col md={3}></Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col md={3}>{objAttrs.name}</Col>
                <Col>
                  <textarea rows={5} className={classes.admin_pages__field_editable} style={{minWidth: '100%', readonly:'readonly' }}
                            onChange={(e) => {}} value={objAttrs.description} />
                </Col>
                <Col md={3}></Col>
                <Col md={3}></Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col sm={{span: 12}}>{t("I18N_Config")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col sm={{span: 12}}>
                  <textarea className={classes.admin_pages__field_editable} rows={10} style={{minWidth: '100%', minHeight:'400px', readonly:'readonly' }}
                            onChange={(e) => {}} value={objAttrs.config} />
                </Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Created_by")}:</Col>
                <Col>{t("I18N_Time_created")}:</Col>
                <Col>{t("I18N_Updated_by")}:</Col>
                <Col>{t("I18N_Time_updated")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col style={{overflowWrap:'anywhere'}}>{objAttrs.creatorUsername}</Col>
                <Col>{formatDate(objAttrs.timeCreated)}</Col>
                <Col style={{overflowWrap:'anywhere'}}>{objAttrs.updaterUsername}</Col>
                <Col>{formatDate(objAttrs.timeUpdated)}</Col>
              </Row>

            </Container>
          }
        </div>


        <hr className="solid" style={{ marginTop: '30px' }}></hr>

        <div className={classes.admin_pages__page_subtitle} >
          <Form.Group as={Row} className="mb-2 mt-3">
            <div style={{ display: "flex" }}>
              <Button size="sm" variant="outline-secondary" className="mb-3"
                style={{ fontSize: '12px', fontWeight: '300' }}
                onClick={handleClose}>
                {t("I18N_Close")}
              </Button>
              <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                {errMsg}
              </span>
            </div>
{/*
              <span className={classes.admin_pages__field_label} style={{ color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                {updateObjErrMsg}
              </span>
*/}
          </Form.Group>
        </div>

      </Card>
    </>
  );
}

export default BizConfigAdminDetailsView;
